<script>
import navigation from "../global/navigation.vue";
import { getAllArticles, getCategories, searchArticles } from "../../kb";

export default {
  name: "kb-list",
  components: {
    navigation,
  },
  data() {
    return {
      search: "",
      selectedCategory: "",
      sidebarCollapsed: false,
      categories: getCategories(),
      totalCount: getAllArticles().length,
    };
  },
  computed: {
    filteredArticles() {
      return searchArticles(this.search, this.selectedCategory);
    },
    hasActiveFilters() {
      return this.search.trim().length > 0 || this.selectedCategory !== "";
    },
    selectedCategoryName() {
      const cat = this.categories.find((c) => c.slug === this.selectedCategory);
      return cat ? cat.name : "";
    },
  },
  mounted() {
    const collapsed = localStorage.getItem("kb::sidebar::collapsed");
    this.sidebarCollapsed = collapsed === "1";
    const category = this.$route.query.category;
    if (category && this.categories.some((c) => c.slug === category)) {
      this.selectedCategory = category;
    }
  },
  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      localStorage.setItem("kb::sidebar::collapsed", this.sidebarCollapsed ? "1" : "0");
    },
    selectCategory(slug) {
      this.selectedCategory = this.selectedCategory === slug ? "" : slug;
    },
    clearFilters() {
      this.search = "";
      this.selectedCategory = "";
    },
    openArticle(article) {
      this.$router.push("/knowledge-base-article?slug=" + encodeURIComponent(article.slug));
    },
  },
};
</script>

<template>
  <div class="nav-margin">
    <navigation />
    <div class="page-body">
      <div class="proof-shell" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
        <!-- Filter rail -->
        <aside class="filter-rail">
          <div class="rail-head">
            <span class="rail-head-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
              </svg>
              Knowledge Base
              <span class="rail-count">{{ totalCount }}</span>
            </span>
            <button class="rail-collapse-btn" title="Collapse sidebar" @click="toggleSidebar">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="11 17 6 12 11 7"></polyline>
                <polyline points="18 17 13 12 18 7"></polyline>
              </svg>
            </button>
          </div>
          <div class="rail-search">
            <img src="/img/search.svg" class="rail-search-icon" alt="" />
            <input
                v-model="search"
                type="text"
                class="rail-search-input"
                placeholder="Search articles"
            />
          </div>
          <div class="rail-scroll">
            <div class="rail-group">
              <p class="rail-label">Categories</p>
              <ul class="cat-list list-unstyled">
                <li>
                  <button
                      class="cat-item"
                      :class="{ 'is-active': selectedCategory === '' }"
                      @click="selectedCategory = ''"
                  >
                    <span class="cat-name">All Articles</span>
                    <span class="cat-count">{{ totalCount }}</span>
                  </button>
                </li>
                <li v-for="cat in categories" :key="cat.slug">
                  <button
                      class="cat-item"
                      :class="{ 'is-active': selectedCategory === cat.slug }"
                      @click="selectCategory(cat.slug)"
                  >
                    <span class="cat-name">{{ cat.name }}</span>
                    <span class="cat-count">{{ cat.count }}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div class="rail-foot">
            <div class="rail-cta">
              <p class="rail-cta-headline">Still have questions?</p>
              <p class="rail-cta-sub">We're happy to help with any questions or concerns.</p>
              <a
                  href="https://ashoreapp.com/contact/"
                  target="_blank"
                  rel="noopener"
                  class="rail-cta-link"
              >Contact Us
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="7" y1="17" x2="17" y2="7"></line>
                  <polyline points="7 7 17 7 17 17"></polyline>
                </svg>
              </a>
            </div>
            <button class="rail-clear-btn" :disabled="!hasActiveFilters" @click="clearFilters">
              Clear all filters
            </button>
          </div>
        </aside>

        <!-- Main column -->
        <section class="proof-main-col">
          <div class="utility-bar">
            <div class="col-inner col-inner-row">
              <div class="utility-left">
                <button v-if="sidebarCollapsed" class="rail-expand-inline" @click="toggleSidebar">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="13 17 18 12 13 7"></polyline>
                    <polyline points="6 17 11 12 6 7"></polyline>
                  </svg>
                  Filters
                </button>
                <div class="count-cell">
                  <span>{{ filteredArticles.length }} article{{ filteredArticles.length === 1 ? "" : "s" }}</span>
                </div>
              </div>
              <div class="utility-right">
                <span v-if="selectedCategoryName" class="status-badge">{{ selectedCategoryName }}</span>
              </div>
            </div>
          </div>

          <div class="content-view">
            <div class="col-inner">
              <ul v-if="filteredArticles.length" class="proof-rows list-unstyled">
                <li
                    v-for="article in filteredArticles"
                    :key="article.slug"
                    class="proof-row"
                >
                  <div class="proof-row-main" @click="openArticle(article)">
                    <div class="proof-main">
                      <a
                          class="proof-name"
                          :href="'/knowledge-base-article?slug=' + article.slug"
                          @click.prevent="openArticle(article)"
                      >{{ article.title }}</a>
                      <div class="proof-sub">
                        <span class="status-badge">
                          <span class="status-dot" style="background: #019256;"></span>
                          {{ article.category }}
                        </span>
                        <span class="article-excerpt">{{ article.excerpt }}</span>
                      </div>
                    </div>
                    <div class="proof-actions">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </div>
                </li>
              </ul>
              <div v-else class="noresults-wrap">
                <p class="noresults-headline">No articles found</p>
                <p class="noresults-sub">Try a different search or clear your filters.</p>
                <button class="rail-clear-btn noresults-clear" @click="clearFilters">Clear all filters</button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-body {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
}
@media (max-width: 991px) {
  .page-body {
    height: auto;
    min-height: 100vh;
  }
}

.proof-shell {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  background: #fff;
  position: relative;
}

/* --- Filter rail (sidebar) --- */
.filter-rail {
  width: 288px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fbfbfc;
  border-right: 1px solid rgba(15, 23, 42, 0.08);
  transition: width 220ms cubic-bezier(0.4, 0, 0.2, 1),
              transform 220ms cubic-bezier(0.4, 0, 0.2, 1),
              opacity 160ms ease;
  overflow: hidden;
  position: relative;
}
.proof-shell.sidebar-collapsed .filter-rail {
  width: 0;
  border-right-width: 0;
  opacity: 0;
  pointer-events: none;
}

.rail-head {
  flex-shrink: 0;
  height: 57px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.rail-head-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.rail-head-title svg { color: #019256; flex-shrink: 0; }
.rail-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
}
.rail-collapse-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.rail-collapse-btn:hover { background: rgba(15, 23, 42, 0.06); color: #111827; }

.rail-search {
  flex-shrink: 0;
  position: relative;
  min-height: 56px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: #fbfbfc;
}
.rail-search-icon {
  position: absolute;
  left: 26px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.55;
  pointer-events: none;
  z-index: 1;
}
.rail-search-input {
  width: 100%;
  height: 34px;
  padding: 0 12px 0 34px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #1f2937;
  background: #fff;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.rail-search-input:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.rail-search-input::placeholder { color: #9ca3af; }

.rail-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scrollbar-width: thin;
}
.rail-scroll::-webkit-scrollbar { width: 8px; }
.rail-scroll::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  border: 2px solid #fbfbfc;
}
.rail-scroll::-webkit-scrollbar-thumb:hover { background: rgba(15, 23, 42, 0.22); }

.rail-group { display: flex; flex-direction: column; gap: 6px; }
.rail-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0;
}

.cat-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
}
.cat-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #1f2937;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.cat-item:hover { background: rgba(1, 146, 86, 0.08); color: #019256; }
.cat-item.is-active {
  background: rgba(1, 146, 86, 0.1);
  color: #019256;
  font-weight: 600;
}
.cat-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cat-count {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
}
.cat-item.is-active .cat-count { color: #019256; }

.rail-foot {
  flex-shrink: 0;
  padding: 12px 14px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fbfbfc;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rail-cta {
  padding: 12px 14px;
  background: rgba(1, 146, 86, 0.05);
  border: 1px solid rgba(1, 146, 86, 0.15);
  border-radius: 8px;
}
.rail-cta-headline {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 2px;
}
.rail-cta-sub {
  font-size: 12px;
  color: #4b5563;
  line-height: 1.45;
  margin: 0 0 8px;
}
.rail-cta-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #019256;
  text-decoration: none;
}
.rail-cta-link:hover {
  color: #017a48;
  text-decoration: none;
}
.rail-clear-btn {
  width: 100%;
  background: transparent;
  color: #4b5563;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;
}
.rail-clear-btn:hover:not(:disabled) {
  background: rgba(1, 146, 86, 0.08);
  color: #019256;
}
.rail-clear-btn:disabled { color: #c2c8d0; cursor: not-allowed; }

.rail-expand-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  background: transparent;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  color: #019256;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.rail-expand-inline svg { color: #019256; }
.rail-expand-inline:hover {
  background: rgba(1, 146, 86, 0.08);
  border-color: rgba(1, 146, 86, 0.35);
}

/* --- Main column --- */
.proof-main-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #f4f5f7;
}

.utility-bar {
  flex-shrink: 0;
  height: 57px;
  display: flex;
  align-items: stretch;
  background: transparent;
}
.utility-bar > .col-inner {
  height: 100%;
  padding: 0 20px;
  background: #fff;
  border-left: 1px solid rgba(15, 23, 42, 0.06);
  border-right: 1px solid rgba(15, 23, 42, 0.06);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.utility-left,
.utility-right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.col-inner {
  width: 100%;
  max-width: 991px;
  margin: 0 auto;
}
.col-inner-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.content-view > .col-inner {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: #fff;
  border-left: 1px solid rgba(15, 23, 42, 0.06);
  border-right: 1px solid rgba(15, 23, 42, 0.06);
}

.count-cell {
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
}

.content-view {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: transparent;
}

/* Article rows */
.proof-rows {
  background: #fff;
  margin: 0;
}
.proof-row {
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  position: relative;
  transition: background-color 120ms ease;
}
.proof-row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #019256;
  opacity: 0;
  transition: opacity 120ms ease;
}
.proof-row:hover { background: #f6f7f9; }
.proof-row:hover::before { opacity: 1; }

.proof-row-main {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 16px;
  cursor: pointer;
  min-width: 0;
}
.proof-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.proof-name {
  display: inline-block;
  max-width: 100%;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 120ms ease;
}
.proof-name:hover { color: #019256; text-decoration: none; }
.proof-sub {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  font-size: 12px;
  color: #6b7280;
  min-width: 0;
}
.article-excerpt {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
  white-space: nowrap;
}
.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 999px;
  flex-shrink: 0;
}
.proof-actions {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: #9ca3af;
}

/* Empty state */
.noresults-wrap {
  padding: 64px 24px;
  text-align: center;
}
.noresults-headline {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
}
.noresults-sub {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 16px;
}
.noresults-clear {
  width: auto;
  display: inline-block;
  border: 1px solid rgba(15, 23, 42, 0.12);
}
</style>
